import React from 'react';
import moment from 'moment';
import { Box } from '@mui/material';

import TextField from 'modules/views/shared/TextField';
import DatePicker from 'modules/views/shared/DatePicker';
import Select from 'modules/views/shared/Select';
import RadioGroup from 'modules/views/shared/RadioGroup';
import SkippableFieldIndicator from 'modules/views/shared/SkippableFieldIndicator';
import PiiFieldWrapper from 'modules/views/shared/PiiFieldWrapper';

export enum FieldType {
  Text = 'text',
  Date = 'date',
  Picker = 'picker',
  Radio = 'radio',
}

export interface IField {
  id: string;
  name: string;
  type: FieldType;
  isRequired: boolean;
  isVisible: boolean;
  options?: Array<{ name: string; id: string }>;
  extraProps?: any;
  /** Whether to show 'required but skippable' indicator */
  showSkippableIndicator?: boolean;
  /** Whether the field is skippable */
  isSkippable?: boolean;
  /** Skippable till type */
  skippableTill?: number;
}

interface IFieldWrapperProps {
  field: IField;
  index: number;
  value?: string;
  setValue: (value: any) => void;
  error?: string;
  options?: Array<{ name: string; id: string }>;
  disabled?: boolean;
  styleClass?: string;
  onPiiFieldClick?: () => void;
}

export const FieldWrapper: React.FC<IFieldWrapperProps> = ({
  field,
  index,
  value,
  setValue,
  error,
  options,
  disabled,
  styleClass,
  onPiiFieldClick,
}) => {
  let fieldComponent;

  if (field.type === FieldType.Radio) {
    fieldComponent = (
      <RadioGroup
        row={true}
        options={options?.map(o => ({ label: o.name, value: o.id })) || []}
        value={value}
        label={field.name}
        error={error}
        required={field.isRequired}
        disabled={disabled}
        styleClass={styleClass}
        testId={`${field.id}-radio-group`}
        onChange={e => {
          setValue(e.target.value);
        }}
      />
    );
  } else if (field.type === FieldType.Picker) {
    fieldComponent = (
      <Select
        options={options.map(o => ({ label: o.name, value: o.id })) || []}
        onChange={selectedValue => {
          setValue(selectedValue);
        }}
        selectedValue={value}
        disabled={disabled}
        inputLabel={field.name}
        required={field.isRequired}
        error={error}
        testId={`${field.id}-select`}
        valueAsSelectedValue={true}
      />
    );
  } else if (field.type === FieldType.Date) {
    const maxDate = moment().subtract(18, 'years');
    fieldComponent = (
      <DatePicker
        inputLabel={field.name}
        value={value}
        minDate={moment('01/01/1900')}
        maxDate={maxDate}
        defaultCalendarMonth={maxDate}
        error={error}
        required={field.isRequired}
        disabled={disabled}
        testId={`${field.id}-picker`}
        onChange={date => setValue(date)}
      />
    );
  } else {
    fieldComponent = (
      <TextField
        label={field.name}
        autoFocus={index === 0}
        name={field.id}
        testId={`${field.id}-input`}
        required={field.isRequired}
        value={value}
        error={error}
        disabled={disabled}
        onChange={e => setValue(e.target.value)}
        {...(field.extraProps || {})}
      />
    );
  }

  return (
    <Box>
      <PiiFieldWrapper label={field.name} required={field.isRequired} piiFieldName={field.id} onClick={onPiiFieldClick}>
        {fieldComponent}
      </PiiFieldWrapper>
      <SkippableFieldIndicator show={field.showSkippableIndicator || false} />
    </Box>
  );
};
